import { ApiEnvironment } from '../models/api-environment';

/**
 * Changes the API environment for a single endpoint.
 *
 * Motivation. Supporting multiple providers is the use case we ran into in Account
 * Manager. I'm sure there are others. The Diagnostics section allows support
 * personnel to request data across accounts. For example, the logged in account's
 * cloud provider is AWS but the account's data is in the cloud provider Azure. (Or
 * vice versa.) So we use this function to make the final call to Azure without
 * disturbing the logged in account's ability to continue making calls to AWS.
 *
 * Example use:
 * ```
 * // Note: We assume AccountsEnvironment for this example, but it's the same
 * // for any of them.
 *
 * // Assume we do the following somewhere...
 * accountsEnvironment.baseUrl = 'https://AWS.service.com/api';
 *
 * // Then create a new AccountEnvironment.
 * export const environment = new AccountsEnvironment('https://AZURE.service.com/api');
 *
 * // Then...
 *
 * // Change AccountsEnvironment, but just for this call!
 * call(AccountsApi.getConfigurationSettingsApplicationId)
 *     .withApiEnvironment(environment)
 *     .args(21)
 *     .subscribe({
 *         next: (configSettings) => {
 *             // Do something with `configSettings`...
 *             // Source: https://AZURE.service.com/api
 *         },
 *     });
 *
 * // Non-overridden backend calls work just like before.
 * // Note: This happens in parallel with previous call but still uses AWS.
 * AccountsApi.getConfigurationSettingsApplicationId(21).subscribe({
 *       next: (configSettings) => {
 *         // Do something with `configSettings`...
 *         // Source: https://AWS.service.com/api
 *       },
 *     });
 * ```
 */
export function call<TArgs extends unknown[], TOut>(
  endpoint: (...args: TArgs) => TOut
) {
  return {
    withApiEnvironment: function (environment: ApiEnvironment) {
      return {
        args: function (...args: TArgs) {
          return endpoint.apply(environment, args);
        },
      };
    },
  };
}
